import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tabs,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CheckOutlined,
  LinkOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  formatDateTime,
  formatAmount,
  addTextColumnTools,
  addNumberColumnTools,
  addDateColumnTools,
} from "./transactionScheduleUi";
import {
  getReturnAllocationOptions,
  confirmDetectedReturn,
  ignoreDetectedReturn,
  restoreIgnoredReturn,
} from "../../api/transactionScheduleApi";
import dayjs from "dayjs";
import TableMetaBar from "../common/TableMetaBar";

const { Text } = Typography;

function renderLongText(value, maxWidth = 320) {
  const text = value || "-";

  return (
    <Tooltip title={text}>
      <div
        style={{
          maxWidth,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </div>
    </Tooltip>
  );
}

function SuggestionOptionCard({
  opt,
  isSuggested = false,
  selectedAllocationId,
  setSelectedAllocationId,
  onConfirm,
  confirming,
}) {
  return (
    <div
      style={{
        border:
          selectedAllocationId === opt.allocationId
            ? "2px solid #1677ff"
            : "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 10,
        marginBottom: 10,
        background:
          selectedAllocationId === opt.allocationId ? "#f0f7ff" : "#fff",
      }}
    >
      <div className="flex items-center gap-2 flex-wrap mb-2">
        {isSuggested ? <Tag color="green">Санал болгосон</Tag> : null}
        {opt.sameReceivingBankAccountFirst ? (
          <Tag color="blue">Ижил данс</Tag>
        ) : null}
        {opt.allocationCheckStatus === "VERIFIED_FINAL" ? (
          <Tag color="blue">Эцэслэн баталгаажсан</Tag>
        ) : (
          <Tag>{opt.allocationCheckStatus || "Нээлттэй цикл"}</Tag>
        )}
        {opt.confidenceLabel ? (
          <Tag
            color={
              opt.confidenceLabel === "Strong"
                ? "green"
                : opt.confidenceLabel === "Possible"
                  ? "orange"
                  : "default"
            }
          >
            {opt.confidenceLabel}
          </Tag>
        ) : null}
        {typeof opt.score === "number" ? (
          <Text type="secondary">Оноо: {opt.score}</Text>
        ) : null}
      </div>

      <div>Хүсэлтийн дугаар: {opt.requestNumber || "-"}</div>
      <div>Төлөгч: {opt.payer || "-"}</div>
      <div>Хүлээн авагч: {opt.beneficiaryCompany || "-"}</div>
      <div>Хүлээн авагчийн данс: {opt.beneficiaryAccount || "-"}</div>
      <div>Гүйлгээний утга: {opt.paymentPurpose || "-"}</div>
      <div>
        Хуваарилсан дүн: {formatAmount(opt.allocatedAmount)}{" "}
        {opt.currency || ""}
      </div>

      <div className="mt-3 flex gap-2">
        <Button
          size="small"
          onClick={() => setSelectedAllocationId(opt.allocationId)}
        >
          Сонгох
        </Button>

        <Popconfirm
          title="Буцаалтыг баталгаажуулах уу?"
          description="Энэ үйлдэл буцаалтыг холбож, буцсан төлөвт оруулж, хуваарилалтыг дахин тохируулна."
          okText="Батлах"
          cancelText="Болих"
          onConfirm={() => onConfirm(opt.allocationId)}
        >
          <Button size="small" type="primary" loading={confirming}>
            Батлах
          </Button>
        </Popconfirm>
      </div>
    </div>
  );
}

export function ReturnActionCell({
  record,
  selectedRecord,
  refreshDetails,
  loadTransactions,
  loadUnmatchedReturns,
  loadIgnoredReturns,
  onConfirmed,
  mode = "incoming",
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedAllocationId, setSelectedAllocationId] = useState(undefined);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [ignoring, setIgnoring] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmedReturnAt, setConfirmedReturnAt] = useState(
    record?.transactionDate ? dayjs(record.transactionDate) : dayjs()
  );

  const loadOptions = useCallback(
    async (queryText = "") => {
      setLoadingOptions(true);
      try {
        const data = await getReturnAllocationOptions(
          record.statementId,
          queryText,
        );
        setOptions(data || []);
      } catch (error) {
        message.error(
          error.message || "Хуваарилалтын сонголтууд ачаалж чадсангүй",
        );
      } finally {
        setLoadingOptions(false);
      }
    },
    [record.statementId],
  );

  useEffect(() => {
    setConfirmedReturnAt(
      record?.transactionDate ? dayjs(record.transactionDate) : dayjs()
    );
  }, [record]);

  useEffect(() => {
    if (modalOpen) {
      loadOptions(search);
    }
  }, [modalOpen, loadOptions, search]);

  const suggestionIds = new Set(
    (record.suggestions || []).map((x) => x.allocationId),
  );

  const sortedOptions = [...options].sort((a, b) => {
    const sameBankA = a.sameReceivingBankAccountFirst ? 1 : 0;
    const sameBankB = b.sameReceivingBankAccountFirst ? 1 : 0;
    if (sameBankB !== sameBankA) return sameBankB - sameBankA;

    const verifiedA = a.allocationCheckStatus === "VERIFIED_FINAL" ? 1 : 0;
    const verifiedB = b.allocationCheckStatus === "VERIFIED_FINAL" ? 1 : 0;
    if (verifiedA !== verifiedB) return verifiedA - verifiedB;

    return (b.score || 0) - (a.score || 0);
  });

  useEffect(() => {
    if (!selectedAllocationId) {
      const firstSuggested = (record.suggestions || [])[0]?.allocationId;
      const firstOption = sortedOptions[0]?.allocationId;
      setSelectedAllocationId(firstSuggested || firstOption);
    }
  }, [record.suggestions, sortedOptions, selectedAllocationId]);

  const refreshAll = async () => {
    await Promise.all([
      loadTransactions?.(),
      loadUnmatchedReturns?.(),
      loadIgnoredReturns?.(),
    ]);

    if (selectedRecord?.id) {
      await refreshDetails?.(selectedRecord.id);
    }

    await onConfirmed?.();
  };

  const confirmForAllocation = async (allocationId) => {
    if (!allocationId) {
      message.warning("Хуваарилалт сонгоно уу");
      return;
    }
    if (!confirmedReturnAt) {
      message.warning("Буцаалтын огноо сонгоно уу");
      return;
    }

    setConfirming(true);
    try {
      await confirmDetectedReturn(record.statementId, {
        allocationId,
        confirmedReturnAt: confirmedReturnAt
          ? confirmedReturnAt.format("YYYY-MM-DDTHH:mm:ss")
          : null,
      });
      message.success("Буцаалт баталгаажлаа");
      await refreshAll();
      setModalOpen(false);
    } catch (error) {
      message.error(error.message || "Буцаалт баталгаажуулж чадсангүй");
    } finally {
      setConfirming(false);
    }
  };

  const handleIgnore = async () => {
    setIgnoring(true);
    try {
      await ignoreDetectedReturn(record.statementId, {});
      message.success("Буцаалт биш гэж тэмдэглэлээ");
      await refreshAll();
    } catch (error) {
      message.error(error.message || "Буцаалт биш гэж тэмдэглэж чадсангүй");
    } finally {
      setIgnoring(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await restoreIgnoredReturn(record.statementId);
      message.success("Буцаалтыг дахин идэвхжүүллээ");
      await refreshAll();
    } catch (error) {
      message.error(error.message || "Сэргээж чадсангүй");
    } finally {
      setRestoring(false);
    }
  };

  const openCycleOptions = sortedOptions.filter(
    (x) =>
      x.allocationCheckStatus !== "VERIFIED_FINAL" &&
      !suggestionIds.has(x.allocationId),
  );
  const verifiedFinalOptions = sortedOptions.filter(
    (x) =>
      x.allocationCheckStatus === "VERIFIED_FINAL" &&
      !suggestionIds.has(x.allocationId),
  );

  return (
    <>
      <Space wrap>
        <Tooltip title="Холбох">
          <Button
            type="primary"
            icon={<LinkOutlined />}
            onClick={() => setModalOpen(true)}
          />
        </Tooltip>

        {mode === "incoming" ? (
          <Tooltip title="Буцаалт биш">
            <Popconfirm
              title="Буцаалт биш гэж тэмдэглэх үү?"
              description="Энэ гүйлгээг нээлттэй буцаалтаас гаргаж, Буцаалт биш хэсэг рүү шилжүүлнэ."
              okText="Тийм"
              cancelText="Болих"
              onConfirm={handleIgnore}
            >
              <Button danger loading={ignoring} icon={<StopOutlined />} />
            </Popconfirm>
          </Tooltip>
        ) : (
          <Tooltip title="Сэргээх">
            <Popconfirm
              title="Буцаалтыг дахин идэвхжүүлэх үү?"
              description="Энэ гүйлгээ Нээлттэй буцаалтууд хэсэг рүү буцна."
              okText="Сэргээх"
              cancelText="Болих"
              onConfirm={handleRestore}
            >
              <Button loading={restoring} icon={<ReloadOutlined />} />
            </Popconfirm>
          </Tooltip>
        )}
      </Space>

      <Modal
        title={`Буцаалт холбох #${record.statementId}`}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={1100}
        destroyOnClose
      >
        <div className="space-y-4">
          <Card size="small">
            <div>
              <strong>Орсон огноо:</strong>{" "}
              {formatDateTime(record.transactionDate)}
            </div>
            <div>
              <strong>Байгууллага:</strong> {record.institutionName || "-"}
            </div>
            <div>
              <strong>Орлого:</strong> {formatAmount(record.income)}{" "}
              {record.currency || ""}
            </div>
            <div>
              <strong>Шилжүүлэгч:</strong> {record.senderName || "-"}
            </div>
            <div>
              <strong>Утга:</strong> {record.description || "-"}
            </div>
          </Card>

          <Card size="small">
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Буцаалтын огноо, цаг
            </div>

            <DatePicker
              showTime
              value={confirmedReturnAt}
              onChange={setConfirmedReturnAt}
              style={{ width: "100%" }}
              format="YYYY-MM-DD HH:mm:ss"
              allowClear={false}
            />

            <div style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
              Анхдагч утга: банкны хуулгын огноо
            </div>
          </Card>

          {(record.suggestions || []).length > 0 ? (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                Санал болгосон ({record.suggestions.length})
              </div>
              {(record.suggestions || []).map((s) => (
                <SuggestionOptionCard
                  key={s.allocationId}
                  opt={s}
                  isSuggested
                  selectedAllocationId={selectedAllocationId}
                  setSelectedAllocationId={setSelectedAllocationId}
                  onConfirm={confirmForAllocation}
                  confirming={confirming}
                />
              ))}
            </div>
          ) : null}

          <div style={{ borderTop: "1px dashed #d9d9d9", paddingTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Гараар хайж сонгох
            </div>

            <Input
              placeholder="Хүсэлт / төлөгч / хүлээн авагч / утгаар хайх"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ marginBottom: 8 }}
              onPressEnter={() => loadOptions(search)}
            />

            <div className="mb-2">
              <Button
                onClick={() => loadOptions(search)}
                loading={loadingOptions}
              >
                Хайх
              </Button>
            </div>

            <Select
              showSearch
              allowClear
              placeholder="Хуваарилалт сонгох"
              style={{ width: "100%", marginBottom: 8 }}
              loading={loadingOptions}
              value={selectedAllocationId}
              onChange={setSelectedAllocationId}
              options={sortedOptions.map((opt) => ({
                label: `${opt.requestNumber || "-"} | Alloc #${opt.allocationId} | ${opt.beneficiaryCompany || "-"} | ${opt.paymentPurpose || "-"} | ${formatAmount(opt.allocatedAmount)} ${opt.currency || ""}`,
                value: opt.allocationId,
              }))}
              filterOption={false}
              onSearch={(value) => setSearch(value)}
            />

            {selectedAllocationId ? (
              <Text type="secondary">
                Сонгосон хуваарилалт: #{selectedAllocationId}
              </Text>
            ) : null}

            <div className="mt-3">
              <Popconfirm
                title="Буцаалтыг баталгаажуулах уу?"
                description="Энэ үйлдэл буцаалтыг холбож, буцсан төлөвт оруулж, хуваарилалтыг дахин тохируулна."
                okText="Батлах"
                cancelText="Болих"
                onConfirm={() => confirmForAllocation(selectedAllocationId)}
              >
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  disabled={!selectedAllocationId}
                  loading={confirming}
                >
                  Буцаалт батлах
                </Button>
              </Popconfirm>
            </div>
          </div>

          {openCycleOptions.length > 0 ? (
            <div style={{ borderTop: "1px dashed #d9d9d9", paddingTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                Нээлттэй циклүүд ({openCycleOptions.length})
              </div>
              {openCycleOptions.slice(0, 5).map((opt) => (
                <SuggestionOptionCard
                  key={opt.allocationId}
                  opt={opt}
                  selectedAllocationId={selectedAllocationId}
                  setSelectedAllocationId={setSelectedAllocationId}
                  onConfirm={confirmForAllocation}
                  confirming={confirming}
                />
              ))}
            </div>
          ) : null}

          {verifiedFinalOptions.length > 0 ? (
            <div style={{ borderTop: "1px dashed #d9d9d9", paddingTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                Эцэслэн баталгаажсан ({verifiedFinalOptions.length})
              </div>
              {verifiedFinalOptions.slice(0, 5).map((opt) => (
                <SuggestionOptionCard
                  key={opt.allocationId}
                  opt={opt}
                  selectedAllocationId={selectedAllocationId}
                  setSelectedAllocationId={setSelectedAllocationId}
                  onConfirm={confirmForAllocation}
                  confirming={confirming}
                />
              ))}
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
}

function UnmatchedReturnsTab({
  unmatchedReturns,
  loadingUnmatchedReturns,
  ignoredReturns,
  loadingIgnoredReturns,
  returnHistory,
  loadingReturnHistory,
  loadTransactions,
  loadUnmatchedReturns,
  loadIgnoredReturns,
  selectedRecord,
  refreshDetails,
  runAllChecksButton,
}) {
  const sortedOpenReturns = useMemo(() => {
    return [...(unmatchedReturns || [])].sort((a, b) => {
      const aTime = a.transactionDate
        ? new Date(a.transactionDate).getTime()
        : 0;
      const bTime = b.transactionDate
        ? new Date(b.transactionDate).getTime()
        : 0;
      const aSuggestions = (a.suggestions || []).length;
      const bSuggestions = (b.suggestions || []).length;

      if (bTime !== aTime) return bTime - aTime;
      return bSuggestions - aSuggestions;
    });
  }, [unmatchedReturns]);

  const institutionOptions = useMemo(() => {
    const map = new Map();

    (unmatchedReturns || []).forEach((item) => {
      if (item.institutionId && item.institutionName) {
        map.set(item.institutionId, item.institutionName);
      }
    });

    return Array.from(map.entries()).map(([value, label]) => ({
      text: label,
      value,
    }));
  }, [unmatchedReturns]);

  const [incomingTableMeta, setIncomingTableMeta] = useState({
    filteredCount: null,
    filters: {},
    sorter: {},
  });

  const [ignoredTableMeta, setIgnoredTableMeta] = useState({
    filteredCount: null,
    filters: {},
    sorter: {},
  });

  const [historyTableMeta, setHistoryTableMeta] = useState({
    filteredCount: null,
    filters: {},
    sorter: {},
  });

  const handleIncomingTableChange = (pagination, filters, sorter, extra) => {
    setIncomingTableMeta({
      filteredCount: Array.isArray(extra?.currentDataSource)
        ? extra.currentDataSource.length
        : null,
      filters: filters || {},
      sorter: sorter || {},
    });
  };

  const handleIgnoredTableChange = (pagination, filters, sorter, extra) => {
    setIgnoredTableMeta({
      filteredCount: Array.isArray(extra?.currentDataSource)
        ? extra.currentDataSource.length
        : null,
      filters: filters || {},
      sorter: sorter || {},
    });
  };

  const handleHistoryTableChange = (pagination, filters, sorter, extra) => {
    setHistoryTableMeta({
      filteredCount: Array.isArray(extra?.currentDataSource)
        ? extra.currentDataSource.length
        : null,
      filters: filters || {},
      sorter: sorter || {},
    });
  };

  const incomingColumns = [
    {
      title: "Орсон огноо",
      dataIndex: "transactionDate",
      key: "transactionDate",
      width: 170,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Байгууллага",
      dataIndex: "institutionName",
      key: "institutionName",
      width: 180,
      filters: institutionOptions,
      filterSearch: true,
      onFilter: (value, record) => record.institutionId === value,
      render: (value) => renderLongText(value, 160),
    },
    {
      title: "Хүлээн авсан данс",
      dataIndex: "receivingAccountNumber",
      key: "receivingAccountNumber",
      width: 180,
      render: (value) => renderLongText(value, 160),
    },
    {
      title: "Орлого",
      key: "income",
      width: 150,
      render: (_, record) =>
        `${formatAmount(record.income)} ${record.currency || ""}`.trim(),
    },
    {
      title: "Шилжүүлэгч",
      dataIndex: "senderName",
      key: "senderName",
      width: 220,
      render: (value) => renderLongText(value, 200),
    },
    {
      title: "Санал",
      key: "suggestionsCount",
      width: 120,
      render: (_, record) => (
        <Tag
          color={(record.suggestions || []).length > 0 ? "orange" : "default"}
        >
          {(record.suggestions || []).length}
        </Tag>
      ),
    },
    {
      title: "Үйлдэл",
      key: "confirmReturn",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <ReturnActionCell
          record={record}
          selectedRecord={selectedRecord}
          refreshDetails={refreshDetails}
          loadTransactions={loadTransactions}
          loadUnmatchedReturns={loadUnmatchedReturns}
          loadIgnoredReturns={loadIgnoredReturns}
        />
      ),
    },
  ];

  const enhancedIncomingColumns = incomingColumns.map((col) => {
    switch (col.key) {
      case "transactionDate":
        return addDateColumnTools(col, "transactionDate", "descend");
      case "institutionName":
        return addTextColumnTools(col, sortedOpenReturns, "institutionName");
      case "receivingAccountNumber":
        return addTextColumnTools(col, sortedOpenReturns, "receivingAccountNumber");
      case "income":
        return {
          ...col,
          sorter: (a, b) => Number(a.income || 0) - Number(b.income || 0),
        };
      case "senderName":
        return addTextColumnTools(col, sortedOpenReturns, "senderName");
      case "suggestionsCount":
        return {
          ...col,
          sorter: (a, b) =>
            Number((a.suggestions || []).length) - Number((b.suggestions || []).length),
        };
      default:
        return col;
    }
  });

  const ignoredColumns = [
    {
      title: "Тэмдэглэсэн огноо",
      dataIndex: "ignoredAt",
      key: "ignoredAt",
      width: 170,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Орсон огноо",
      dataIndex: "transactionDate",
      key: "transactionDate",
      width: 170,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Байгууллага",
      dataIndex: "institutionName",
      key: "institutionName",
      width: 180,
      render: (value) => renderLongText(value, 160),
    },
    {
      title: "Орлого",
      key: "income",
      width: 150,
      render: (_, record) =>
        `${formatAmount(record.income)} ${record.currency || ""}`.trim(),
    },
    {
      title: "Шилжүүлэгч",
      dataIndex: "senderName",
      key: "senderName",
      width: 220,
      render: (value) => renderLongText(value, 200),
    },
    {
      title: "Тэмдэглэсэн",
      dataIndex: "ignoredBy",
      key: "ignoredBy",
      width: 140,
      render: (value) => value || "-",
    },
    {
      title: "Үйлдэл",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <ReturnActionCell
          record={record}
          selectedRecord={selectedRecord}
          refreshDetails={refreshDetails}
          loadTransactions={loadTransactions}
          loadUnmatchedReturns={loadUnmatchedReturns}
          loadIgnoredReturns={loadIgnoredReturns}
          mode="ignored"
        />
      ),
    },
  ];

  const enhancedIgnoredColumns = ignoredColumns.map((col) => {
    switch (col.key) {
      case "ignoredAt":
        return addDateColumnTools(col, "ignoredAt", "descend");
      case "transactionDate":
        return addDateColumnTools(col, "transactionDate");
      case "institutionName":
        return addTextColumnTools(col, ignoredReturns || [], "institutionName");
      case "income":
        return {
          ...col,
          sorter: (a, b) => Number(a.income || 0) - Number(b.income || 0),
        };
      case "senderName":
        return addTextColumnTools(col, ignoredReturns || [], "senderName");
      case "ignoredBy":
        return addTextColumnTools(col, ignoredReturns || [], "ignoredBy");
      default:
        return col;
    }
  });

  const historyColumns = [
    {
      title: "Баталгаажуулсан огноо",
      dataIndex: "confirmedAt",
      key: "confirmedAt",
      width: 170,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Баталгаажуулсан хэрэглэгч",
      dataIndex: "confirmedBy",
      key: "confirmedBy",
      width: 140,
      render: (value) => value || "-",
    },
    {
      title: "Орсон огноо",
      dataIndex: "transactionDate",
      key: "transactionDate",
      width: 170,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Хүсэлтийн дугаар",
      dataIndex: "requestNumber",
      key: "requestNumber",
      width: 160,
      render: (value) => renderLongText(value, 140),
    },
    {
      title: "Төлөгч",
      dataIndex: "payer",
      key: "payer",
      width: 180,
      render: (value) => renderLongText(value, 160),
    },
    {
      title: "Хуваарилсан дүн",
      key: "allocatedAmount",
      width: 160,
      render: (_, record) =>
        `${formatAmount(record.allocatedAmount)} ${record.currency || ""}`.trim(),
    },
    {
      title: "Орлого",
      key: "income",
      width: 140,
      render: (_, record) =>
        `${formatAmount(record.income)} ${record.currency || ""}`.trim(),
    },
  ];

  const enhancedHistoryColumns = historyColumns.map((col) => {
    switch (col.key) {
      case "confirmedAt":
        return addDateColumnTools(col, "confirmedAt", "descend");
      case "confirmedBy":
        return addTextColumnTools(col, returnHistory || [], "confirmedBy");
      case "transactionDate":
        return addDateColumnTools(col, "transactionDate");
      case "requestNumber":
        return addTextColumnTools(col, returnHistory || [], "requestNumber");
      case "payer":
        return addTextColumnTools(col, returnHistory || [], "payer");
      case "allocatedAmount":
        return {
          ...col,
          sorter: (a, b) => Number(a.allocatedAmount || 0) - Number(b.allocatedAmount || 0),
        };
      case "income":
        return {
          ...col,
          sorter: (a, b) => Number(a.income || 0) - Number(b.income || 0),
        };
      default:
        return col;
    }
  });

  return (
    <Card title="Буцаалтууд" extra={runAllChecksButton}>
      <Alert
        type="warning"
        showIcon
        message="Буцаалтын шалгалтын ажлын хэсэг"
        description="Нээлттэй буцаалтууд дээр буцаалт батлах эсвэл Буцаалт биш гэж тэмдэглэнэ. Буцаалт биш хэсгээс дараа нь сэргээж эсвэл гараар холбож болно."
        style={{ marginBottom: 16 }}
      />

      <Tabs
        defaultActiveKey="incoming"
        items={[
          {
            key: "incoming",
            label: `Нээлттэй буцаалтууд (${sortedOpenReturns.length})`,
            children: (
              <>
                <div className="mb-4 flex flex-wrap gap-2">
                  <Tag color="red">Нийт: {sortedOpenReturns.length}</Tag>
                  <Tag color="orange">
                    Саналтай:{" "}
                    {
                      sortedOpenReturns.filter(
                        (x) => (x.suggestions || []).length > 0,
                      ).length
                    }
                  </Tag>
                  <Tag color="default">
                    Саналгүй:{" "}
                    {
                      sortedOpenReturns.filter(
                        (x) => (x.suggestions || []).length === 0,
                      ).length
                    }
                  </Tag>
                </div>
                <TableMetaBar
                  totalCount={sortedOpenReturns.length}
                  visibleCount={incomingTableMeta.filteredCount}
                  filters={incomingTableMeta.filters}
                  sorter={incomingTableMeta.sorter}
                />
                <Table
                  rowKey="statementId"
                  columns={enhancedIncomingColumns}
                  sortDirections={["ascend", "descend"]}
                  dataSource={sortedOpenReturns}
                  loading={loadingUnmatchedReturns}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: "max-content" }}
                  bordered
                  size="small"
                  onChange={handleIncomingTableChange}
                  rowClassName={() => "returned-row"}
                  expandable={{
                    expandedRowRender: (record) => (
                      <Descriptions
                        bordered
                        size="small"
                        column={{ xs: 1, sm: 2, md: 2, lg: 3 }}
                      >
                        <Descriptions.Item label="Утга" span={3}>
                          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {record.description || "-"}
                          </div>
                        </Descriptions.Item>
                        <Descriptions.Item label="Statement ID">
                          {record.statementId}
                        </Descriptions.Item>
                        <Descriptions.Item label="Байгууллагын ID">
                          {record.institutionId || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Валют">
                          {record.currency || "-"}
                        </Descriptions.Item>
                      </Descriptions>
                    ),
                    rowExpandable: () => true,
                  }}
                />
              </>
            ),
          },
          {
            key: "ignored",
            label: `Буцаалт биш (${(ignoredReturns || []).length})`,
            children: (
              <>
                <TableMetaBar
                  totalCount={(ignoredReturns || []).length}
                  visibleCount={ignoredTableMeta.filteredCount}
                  filters={ignoredTableMeta.filters}
                  sorter={ignoredTableMeta.sorter}
                />
                <Table
                  rowKey="statementId"
                  columns={enhancedIgnoredColumns}
                  sortDirections={["ascend", "descend"]}
                  dataSource={ignoredReturns || []}
                  loading={loadingIgnoredReturns}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: "max-content" }}
                  bordered
                  size="small"
                  onChange={handleIgnoredTableChange}
                  expandable={{
                    expandedRowRender: (record) => (
                      <Descriptions
                        bordered
                        size="small"
                        column={{ xs: 1, sm: 2, md: 2 }}
                      >
                        <Descriptions.Item label="Утга" span={2}>
                          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {record.description || "-"}
                          </div>
                        </Descriptions.Item>
                        <Descriptions.Item label="Statement ID">
                          {record.statementId}
                        </Descriptions.Item>
                        <Descriptions.Item label="Валют">
                          {record.currency || "-"}
                        </Descriptions.Item>
                      </Descriptions>
                    ),
                    rowExpandable: () => true,
                  }}
                />
              </>
            ),
          },
          {
            key: "history",
            label: `Баталгаажсан түүх (${(returnHistory || []).length})`,
            children: (
              <>
                <TableMetaBar
                  totalCount={(returnHistory || []).length}
                  visibleCount={historyTableMeta.filteredCount}
                  filters={historyTableMeta.filters}
                  sorter={historyTableMeta.sorter}
                />
                <Table
                  rowKey={(row) =>
                    `${row.statementId}-${row.allocationId}-${row.confirmedAt || ""}`
                  }
                  columns={enhancedHistoryColumns}
                  sortDirections={["ascend", "descend"]}
                  dataSource={returnHistory || []}
                  loading={loadingReturnHistory}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: "max-content" }}
                  bordered
                  size="small"
                  onChange={handleHistoryTableChange}
                  expandable={{
                    expandedRowRender: (record) => (
                      <Descriptions
                        bordered
                        size="small"
                        column={{ xs: 1, sm: 2, md: 2, lg: 3 }}
                      >
                        <Descriptions.Item label="Хүлээн авагч байгууллага">
                          {record.beneficiaryCompany || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Шилжүүлэгчийн нэр">
                          {record.senderName || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Statement ID">
                          {record.statementId || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Allocation ID">
                          {record.allocationId || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Валют">
                          {record.currency || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Утга" span={3}>
                          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {record.description || "-"}
                          </div>
                        </Descriptions.Item>
                      </Descriptions>
                    ),
                    rowExpandable: () => true,
                  }}
                />
              </>
            ),
          },
        ]}
      />
    </Card>
  );
}

export default UnmatchedReturnsTab;